import "./skills.page.css";

import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { calculateSkillBonus, signed } from "../../../calculations/skillCalculations";
import { CORE_FEATS } from "../../../catalogs/featsCatalog";
import { systemLabelKey } from "../../../catalogs/characterSystemCatalog";
import { useD20Roll } from "../../../hooks/useD20Roll";
import RollResultModal from "../RollResultModal";
import PassivesAndTraits from "./PassivesAndTraits";
import SkillsList from "./SkillsList";

const catalogFeatIds = new Set(CORE_FEATS.map((feat) => feat.id));
const normalizeFeats = (feats) => feats.map((feat) => {
  const catalogId = feat.catalogId || (catalogFeatIds.has(feat.id) ? feat.id : null);
  return catalogId ? { id: feat.id, catalogId } : feat;
});
const requiresFeatCleanup = (feats) => feats.some((feat) => {
  const catalogId = feat.catalogId || (catalogFeatIds.has(feat.id) ? feat.id : null);
  return catalogId && (feat.catalogId !== catalogId || Object.keys(feat).some((key) => key !== "id" && key !== "catalogId"));
});

const CharacterSkills = ({ character, onChange }) => {
  const { t } = useTranslation();
  const { result, roll, closeRoll } = useD20Roll();
  const setSkills = (skillsList) => {
    const nextCharacter = { ...character, skills: { ...character.skills, skillsList } };
    onChange(nextCharacter);
  };
  const setPassives = (nextPassives) => {
    const passives = typeof nextPassives === "function"
      ? nextPassives(character.passives)
      : nextPassives;
    onChange({ ...character, passives });
  };
  const setRuleChoice = (choiceId, changes) => onChange({
    ...character,
    ruleChoices: (character.ruleChoices || []).map((choice) => choice.id === choiceId
      ? { ...choice, ...changes }
      : choice),
  });
  const getSkillBonus = (skill) => calculateSkillBonus(character.characteristics, skill);

  useEffect(() => {
    if (requiresFeatCleanup(character.feats || [])) onChange({ ...character, feats: normalizeFeats(character.feats) });
  }, [character, onChange]);

  const rollSkill = (skill) => {
    const bonus = getSkillBonus(skill);
    const name = skill.catalogId ? t(systemLabelKey("skills", skill.catalogId)) : skill.name;
    roll({
      label: t("roll.abilityCheck", { ability: name }),
      modifier: bonus.total,
      modifierBreakdown: [
        { label: t(systemLabelKey("abilities", skill.abilityId)), value: bonus.abilityModifier },
        { label: t("skills.ranksLabel"), value: bonus.ranks },
        { label: t("skills.classSkillLabel"), value: bonus.classSkillBonus },
        { label: t("skills.otherBonus"), value: bonus.otherBonus },
        ...(bonus.ruleBonus ? [{ label: t("skills.ruleBonus", { defaultValue: "Rules" }), value: bonus.ruleBonus }] : []),
      ],
    });
  };

  return (
    <div className="quarter">
      <div className="skills-container">
        <SkillsList
          skills={character.skills.skillsList}
          setSkills={setSkills}
          characterLevel={character.level.level}
          skillRankCapacity={character.ruleDerived?.skillRankCapacity || 0}
          spentSkillRanks={character.ruleDerived?.spentSkillRanks || 0}
          onRoll={rollSkill}
          getTotalBonus={(skill) => signed(getSkillBonus(skill).total)}
        />
        <PassivesAndTraits grantedFeatures={character.grantedFeatures || []} passives={character.passives} ruleChoices={character.ruleChoices || []} ruleDerived={character.ruleDerived || {}} setRuleChoice={setRuleChoice} setPassives={setPassives} feats={character.feats} setFeats={(feats) => onChange({ ...character, feats })} />
      </div>
      <RollResultModal result={result} onClose={closeRoll} />
    </div>
  );
};

export default CharacterSkills;
